import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import LoadingProgress from '../components/LoadingProgress'
import Box from '../components/Box'
import ErrorMessage from '../components/ErrorMessage'

import { fetchOrphanedRecords, syncCustomerToAuth, syncUserToAuth, syncBulkToAuth, fetchAuthOrphanedRecords, importUserFromAuth, importCustomerFromAuth } from '../actions/sync'

class DatabaseSyncView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      syncingCustomerId: null,
      syncingUserId: null,
      syncedCustomers: new Set(),
      syncedUsers: new Set(),
      bulkSyncing: false,
      bulkResult: null,
      importingAuthId: null,
      importedAuthRecords: new Set()
    }

    this.handleSyncCustomer = this.handleSyncCustomer.bind(this)
    this.handleSyncUser = this.handleSyncUser.bind(this)
    this.handleBulkSync = this.handleBulkSync.bind(this)
    this.handleImportUser = this.handleImportUser.bind(this)
    this.handleImportCustomer = this.handleImportCustomer.bind(this)
    this.handleRefreshAll = this.handleRefreshAll.bind(this)
  }

  componentDidMount() {
    this.props.fetchOrphanedRecords()
    this.props.fetchAuthOrphanedRecords()
  }

  handleRefreshAll() {
    this.props.fetchOrphanedRecords()
    this.props.fetchAuthOrphanedRecords()
  }

  handleImportUser(authId) {
    this.setState({ importingAuthId: authId })
    
    this.props.importUserFromAuth(authId)
      .then(response => {
        if (response.error) {
          alert(`Error: ${response.error.message}`)
        } else {
          alert('User imported successfully from Auth database!')
          this.setState(prevState => ({
            importedAuthRecords: new Set([...prevState.importedAuthRecords, authId])
          }))
          // Refresh both lists
          this.props.fetchOrphanedRecords()
          this.props.fetchAuthOrphanedRecords()
        }
        this.setState({ importingAuthId: null })
      })
  }

  handleImportCustomer(authId) {
    this.setState({ importingAuthId: authId })
    
    this.props.importCustomerFromAuth(authId)
      .then(response => {
        if (response.error) {
          alert(`Error: ${response.error.message}`)
        } else {
          alert('Customer imported successfully from Auth database!')
          this.setState(prevState => ({
            importedAuthRecords: new Set([...prevState.importedAuthRecords, authId])
          }))
          // Refresh both lists
          this.props.fetchOrphanedRecords()
          this.props.fetchAuthOrphanedRecords()
        }
        this.setState({ importingAuthId: null })
      })
  }

  handleSyncCustomer(customerId) {
    this.setState({ syncingCustomerId: customerId })
    
    this.props.syncCustomerToAuth(customerId)
      .then(response => {
        if (response.error) {
          alert(`Error: ${response.error.message}`)
        } else {
          alert('Customer synced successfully! Default password: ChangeMe123!')
          this.setState(prevState => ({
            syncedCustomers: new Set([...prevState.syncedCustomers, customerId])
          }))
          // Refresh the list
          this.props.fetchOrphanedRecords()
        }
        this.setState({ syncingCustomerId: null })
      })
  }

  handleSyncUser(userId) {
    this.setState({ syncingUserId: userId })
    
    this.props.syncUserToAuth(userId)
      .then(response => {
        if (response.error) {
          alert(`Error: ${response.error.message}`)
        } else {
          alert('User synced successfully! Default password: ChangeMe123!')
          this.setState(prevState => ({
            syncedUsers: new Set([...prevState.syncedUsers, userId])
          }))
          // Refresh the list
          this.props.fetchOrphanedRecords()
        }
        this.setState({ syncingUserId: null })
      })
  }

  handleBulkSync() {
    if (!window.confirm('Are you sure you want to sync ALL orphaned records at once? This cannot be undone.')) {
      return
    }

    this.setState({ bulkSyncing: true, bulkResult: null })
    
    this.props.syncBulkToAuth()
      .then(response => {
        if (response.error) {
          alert(`Error: ${response.error.message}`)
        } else {
          this.setState({ bulkResult: response.response.results })
          // Refresh the list
          this.props.fetchOrphanedRecords()
        }
        this.setState({ bulkSyncing: false })
      })
  }

  render() {
    const { orphanedData, authOrphanedData, error, loading } = this.props
    const { syncingCustomerId, syncingUserId, syncedCustomers, syncedUsers, bulkSyncing, bulkResult, importingAuthId, importedAuthRecords } = this.state

    if (error) {
      return <ErrorMessage message={error.message} />
    }

    if ((!orphanedData && !authOrphanedData) || loading) {
      return <LoadingProgress />
    }

    const customers = orphanedData?.customers || []
    const users = orphanedData?.users || []
    const summary = orphanedData?.summary || {}
    const analysis = orphanedData?.analysis || null
    const details = orphanedData?.details || null

    const authCustomers = authOrphanedData?.customers || []
    const authAdmins = authOrphanedData?.admins || []
    const authSummary = authOrphanedData?.summary || {}

    // Create maps for quick lookup
    const orphanedCustomerIds = new Set(customers.map(c => c._id))
    const orphanedUserIds = new Set(users.map(u => u._id))
    const authCustomerIds = new Set(authCustomers.map(c => c._id))
    const authAdminIds = new Set(authAdmins.map(u => u._id))

    // Build combined customer list (all unique customers from both databases)
    const allCustomersMap = new Map()
    
    // Add orphaned local customers
    customers.forEach(c => {
      allCustomersMap.set(c._id, {
        _id: c._id,
        fullname: c.Fullname,
        email: c.Email,
        type: c.Type,
        category: c.Category,
        group: c.Group,
        inLocal: true,
        inAuth: false
      })
    })
    
    // Add orphaned auth customers
    authCustomers.forEach(c => {
      allCustomersMap.set(c._id, {
        _id: c._id,
        fullname: c.fullname,
        email: c.email,
        type: c.type,
        category: c.category,
        group: c.group,
        inLocal: false,
        inAuth: true
      })
    })

    // Build combined user list
    const allUsersMap = new Map()
    
    // Add orphaned local users
    users.forEach(u => {
      allUsersMap.set(u._id, {
        _id: u._id,
        fullname: u.fullname,
        email: u.email,
        type: u.type,
        category: u.category,
        active: u.active,
        inLocal: true,
        inAuth: false
      })
    })
    
    // Add orphaned auth users
    authAdmins.forEach(u => {
      allUsersMap.set(u._id, {
        _id: u._id,
        fullname: u.fullname,
        email: u.email,
        type: u.type,
        category: u.category,
        active: true,
        inLocal: false,
        inAuth: true
      })
    })

    const combinedCustomers = Array.from(allCustomersMap.values())
    const combinedUsers = Array.from(allUsersMap.values())

    return (
      <div>
        <div className="row mb-2">
          <div className="col-xs-12 col-sm-8 col-md-8">
            <h1>
              <span className="text-vw-dark">DATABASE </span>
              <span className="text-vw-light">SYNCHRONIZATION</span>
            </h1>
          </div>
          <div className="col-xs-12 col-sm-4 col-md-4 text-right">
            <button 
              className="btn btn-warning mr-2"
              onClick={this.handleRefreshAll}
              disabled={loading}
            >
              <i className="fas fa-sync mr-1"></i>Refresh All
            </button>
            {(customers.length > 0 || users.length > 0) && (
              <button 
                className="btn btn-danger"
                onClick={this.handleBulkSync}
                disabled={bulkSyncing}
              >
                <i className="fas fa-bolt mr-1"></i>
                {bulkSyncing ? 'Syncing...' : 'Bulk Sync All'}
              </button>
            )}
          </div>
        </div>

        <hr className="bg-vw-light" />

        {/* Summary */}
        <Box title="Database Summary" icon="database">
          <div className="row mb-4">
            <div className="col-md-6">
              <h4 className="text-vw-dark mb-3">
                <i className="fas fa-users mr-2"></i>Customers
              </h4>
              <div className="row">
                <div className="col-md-4">
                  <div className="alert alert-info text-center">
                    <small className="d-block">In Local DB</small>
                    <h3 className="mb-0">{summary.totalCustomersLocal || 0}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="alert alert-primary text-center">
                    <small className="d-block">In Auth DB</small>
                    <h3 className="mb-0">{summary.totalCustomersAuth || 0}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="alert alert-warning text-center">
                    <small className="d-block">Orphaned</small>
                    <h3 className="mb-0">{combinedCustomers.length}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <h4 className="text-vw-dark mb-3">
                <i className="fas fa-user-cog mr-2"></i>Users
              </h4>
              <div className="row">
                <div className="col-md-4">
                  <div className="alert alert-info text-center">
                    <small className="d-block">In Local DB</small>
                    <h3 className="mb-0">{summary.totalUsersLocal || 0}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="alert alert-primary text-center">
                    <small className="d-block">In Auth DB</small>
                    <h3 className="mb-0">{summary.totalUsersAuth || 0}</h3>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="alert alert-warning text-center">
                    <small className="d-block">Orphaned</small>
                    <h3 className="mb-0">{combinedUsers.length}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Box>

        {/* Analysis Details */}
        {analysis && (
          <Box title="Detailed Analysis" icon="search">
            <div className="row">
              <div className="col-md-6">
                <h4 className="text-vw-dark">Customers Analysis</h4>
                <table className="table table-sm">
                  <tbody>
                    <tr><td>Perfect Match (same email & ID)</td><td className="text-success"><strong>{analysis.customers.perfectMatch}</strong></td></tr>
                    <tr><td>Orphaned (not in Auth)</td><td className="text-warning"><strong>{analysis.customers.orphaned}</strong></td></tr>
                    <tr><td>Different IDs (same email)</td><td className="text-info"><strong>{analysis.customers.differentIds}</strong></td></tr>
                    <tr><td>Name Mismatch</td><td className="text-danger"><strong>{analysis.customers.nameMismatch}</strong></td></tr>
                    <tr><td>No Email</td><td className="text-muted"><strong>{analysis.customers.noEmail}</strong></td></tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <h4 className="text-vw-dark">Users Analysis</h4>
                <table className="table table-sm">
                  <tbody>
                    <tr><td>Perfect Match (same email & ID)</td><td className="text-success"><strong>{analysis.users.perfectMatch}</strong></td></tr>
                    <tr><td>Orphaned (not in Auth)</td><td className="text-warning"><strong>{analysis.users.orphaned}</strong></td></tr>
                    <tr><td>Different IDs (same email)</td><td className="text-info"><strong>{analysis.users.differentIds}</strong></td></tr>
                    <tr><td>Name Mismatch</td><td className="text-danger"><strong>{analysis.users.nameMismatch}</strong></td></tr>
                    <tr><td>No Email</td><td className="text-muted"><strong>{analysis.users.noEmail}</strong></td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Box>
        )}

        {/* Issues Details */}
        {details && (details.matchedCustomers.length > 0 || details.duplicateCustomers.length > 0 || 
                     details.noEmailCustomers.length > 0 || details.matchedUsers.length > 0 || 
                     details.duplicateUsers.length > 0 || details.noEmailUsers.length > 0) && (
          <Box title="Issues Found" icon="alert-triangle">
            {details.matchedCustomers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-info">Customers with Same Email but Different IDs</h5>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead><tr><th>Email</th><th>ONE ID</th><th>Auth ID</th><th>ONE Name</th><th>Auth Name</th></tr></thead>
                    <tbody>
                      {details.matchedCustomers.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.email}</td>
                          <td><code>{item.oneId}</code></td>
                          <td><code>{item.authId}</code></td>
                          <td>{item.oneFullname}</td>
                          <td>{item.authFullname}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {details.duplicateCustomers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-danger">Customers with Name Mismatches</h5>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead><tr><th>Email</th><th>ONE Name</th><th>Auth Name</th></tr></thead>
                    <tbody>
                      {details.duplicateCustomers.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.email}</td>
                          <td>{item.oneFullname}</td>
                          <td>{item.authFullname}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {details.noEmailCustomers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-muted">Customers Without Email</h5>
                <ul>
                  {details.noEmailCustomers.map((item, idx) => (
                    <li key={idx}>{item.Fullname} <code>({item._id})</code></li>
                  ))}
                </ul>
              </div>
            )}

            {details.matchedUsers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-info">Users with Same Email but Different IDs</h5>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead><tr><th>Email</th><th>ONE ID</th><th>Auth ID</th><th>ONE Name</th><th>Auth Name</th></tr></thead>
                    <tbody>
                      {details.matchedUsers.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.email}</td>
                          <td><code>{item.oneId}</code></td>
                          <td><code>{item.authId}</code></td>
                          <td>{item.oneFullname}</td>
                          <td>{item.authFullname}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {details.duplicateUsers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-danger">Users with Name Mismatches</h5>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead><tr><th>Email</th><th>ONE Name</th><th>Auth Name</th></tr></thead>
                    <tbody>
                      {details.duplicateUsers.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.email}</td>
                          <td>{item.oneFullname}</td>
                          <td>{item.authFullname}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {details.noEmailUsers.length > 0 && (
              <div className="mb-4">
                <h5 className="text-muted">Users Without Email</h5>
                <ul>
                  {details.noEmailUsers.map((item, idx) => (
                    <li key={idx}>{item.fullname} <code>({item._id})</code></li>
                  ))}
                </ul>
              </div>
            )}
          </Box>
        )}

        {/* Bulk Sync Results */}
        {bulkResult && (
          <Box title="Bulk Sync Results" icon="check-circle">
            <div className="row">
              <div className="col-md-6">
                <h5>Customers</h5>
                <p>Total: {bulkResult.customers.total}</p>
                <p className="text-success">Success: {bulkResult.customers.success}</p>
                <p className="text-danger">Failed: {bulkResult.customers.failed}</p>
                {bulkResult.customers.failedList.length > 0 && (
                  <div>
                    <strong>Failed:</strong>
                    <ul>
                      {bulkResult.customers.failedList.map(item => (
                        <li key={item._id}>{item.fullname} - {item.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <h5>Users</h5>
                <p>Total: {bulkResult.users.total}</p>
                <p className="text-success">Success: {bulkResult.users.success}</p>
                <p className="text-danger">Failed: {bulkResult.users.failed}</p>
                {bulkResult.users.failedList.length > 0 && (
                  <div>
                    <strong>Failed:</strong>
                    <ul>
                      {bulkResult.users.failedList.map(item => (
                        <li key={item._id}>{item.fullname} - {item.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </Box>
        )}

        {/* Orphaned Customers */}
        <Box title={`Orphaned Customers (${combinedCustomers.length})`} icon="users">
          {combinedCustomers.length === 0 ? (
            <div className="alert alert-success">
              <i className="fas fa-check-circle mr-2"></i>
              All customers are synced between Local and Auth databases!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th>Status</th>
                    <th>ID</th>
                    <th>Fullname</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedCustomers.map(customer => (
                    <tr key={customer._id} className={
                      (syncedCustomers.has(customer._id) || importedAuthRecords.has(customer._id)) ? 'table-success' : ''
                    }>
                      <td>
                        {customer.inLocal && !customer.inAuth && (
                          <span className="badge badge-warning">Local Only</span>
                        )}
                        {!customer.inLocal && customer.inAuth && (
                          <span className="badge badge-info">Auth Only</span>
                        )}
                      </td>
                      <td><code>{customer._id}</code></td>
                      <td>{customer.fullname}</td>
                      <td>{customer.email || <em className="text-muted">No email</em>}</td>
                      <td>{customer.type}</td>
                      <td>{customer.category}</td>
                      <td>
                        {customer.inLocal && !customer.inAuth && (
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => this.handleSyncCustomer(customer._id)}
                            disabled={syncingCustomerId === customer._id || syncedCustomers.has(customer._id)}
                          >
                            {syncingCustomerId === customer._id ? (
                              <>
                                <i className="fas fa-spinner fa-spin mr-1"></i>Syncing...
                              </>
                            ) : syncedCustomers.has(customer._id) ? (
                              <>
                                <i className="fas fa-check mr-1"></i>Synced
                              </>
                            ) : (
                              <>
                                <i className="fas fa-arrow-right mr-1"></i>Sync to Auth
                              </>
                            )}
                          </button>
                        )}
                        {!customer.inLocal && customer.inAuth && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => this.handleImportCustomer(customer._id)}
                            disabled={importingAuthId === customer._id || importedAuthRecords.has(customer._id)}
                          >
                            {importingAuthId === customer._id ? (
                              <>
                                <i className="fas fa-spinner fa-spin mr-1"></i>Importing...
                              </>
                            ) : importedAuthRecords.has(customer._id) ? (
                              <>
                                <i className="fas fa-check mr-1"></i>Imported
                              </>
                            ) : (
                              <>
                                <i className="fas fa-arrow-left mr-1"></i>Import to Local
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Box>

        <br />

        {/* Orphaned Users */}
        <Box title={`Orphaned Users (${combinedUsers.length})`} icon="user-cog">
          {combinedUsers.length === 0 ? (
            <div className="alert alert-success">
              <i className="fas fa-check-circle mr-2"></i>
              All users are synced between Local and Auth databases!
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th>Status</th>
                    <th>ID</th>
                    <th>Fullname</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedUsers.map(user => (
                    <tr key={user._id} className={
                      (syncedUsers.has(user._id) || importedAuthRecords.has(user._id)) ? 'table-success' : ''
                    }>
                      <td>
                        {user.inLocal && !user.inAuth && (
                          <span className="badge badge-warning">Local Only</span>
                        )}
                        {!user.inLocal && user.inAuth && (
                          <span className="badge badge-info">Auth Only</span>
                        )}
                      </td>
                      <td><code>{user._id}</code></td>
                      <td>{user.fullname}</td>
                      <td>{user.email || <em className="text-muted">No email</em>}</td>
                      <td>{user.type}</td>
                      <td>{user.active ? 'Yes' : 'No'}</td>
                      <td>
                        {user.inLocal && !user.inAuth && (
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => this.handleSyncUser(user._id)}
                            disabled={syncingUserId === user._id || syncedUsers.has(user._id)}
                          >
                            {syncingUserId === user._id ? (
                              <>
                                <i className="fas fa-spinner fa-spin mr-1"></i>Syncing...
                              </>
                            ) : syncedUsers.has(user._id) ? (
                              <>
                                <i className="fas fa-check mr-1"></i>Synced
                              </>
                            ) : (
                              <>
                                <i className="fas fa-arrow-right mr-1"></i>Sync to Auth
                              </>
                            )}
                          </button>
                        )}
                        {!user.inLocal && user.inAuth && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => this.handleImportUser(user._id)}
                            disabled={importingAuthId === user._id || importedAuthRecords.has(user._id)}
                          >
                            {importingAuthId === user._id ? (
                              <>
                                <i className="fas fa-spinner fa-spin mr-1"></i>Importing...
                              </>
                            ) : importedAuthRecords.has(user._id) ? (
                              <>
                                <i className="fas fa-check mr-1"></i>Imported
                              </>
                            ) : (
                              <>
                                <i className="fas fa-arrow-left mr-1"></i>Import to Local
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Box>

        <div className="alert alert-info mt-3">
          <h5><i className="fas fa-info-circle mr-2"></i>Important Notes:</h5>
          <ul>
            <li>Sync uses <strong>email as the primary identifier</strong> to match records</li>
            <li>Existing passwords (salt/hash) are <strong>copied between databases</strong></li>
            <li>If no password exists, default password is set to: <code>ChangeMe123!</code></li>
            <li>Records without email addresses cannot be synced</li>
            <li>Email conflicts will prevent syncing - manual resolution required</li>
            <li>Check the "Issues Found" section for records with mismatches or conflicts</li>
            <li><strong>Local Only:</strong> Records exist in local database but not in Auth - use "Sync to Auth"</li>
            <li><strong>Auth Only:</strong> Records exist in Auth database but not in local - use "Import to Local"</li>
          </ul>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  orphanedData: state.sync?.orphanedData,
  authOrphanedData: state.sync?.authOrphanedData,
  error: state.sync?.error,
  loading: state.sync?.loading
})

const mapDispatchToProps = dispatch => ({
  fetchOrphanedRecords: bindActionCreators(fetchOrphanedRecords, dispatch),
  fetchAuthOrphanedRecords: bindActionCreators(fetchAuthOrphanedRecords, dispatch),
  syncCustomerToAuth: bindActionCreators(syncCustomerToAuth, dispatch),
  syncUserToAuth: bindActionCreators(syncUserToAuth, dispatch),
  syncBulkToAuth: bindActionCreators(syncBulkToAuth, dispatch),
  importUserFromAuth: bindActionCreators(importUserFromAuth, dispatch),
  importCustomerFromAuth: bindActionCreators(importCustomerFromAuth, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(DatabaseSyncView)
